import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useBulkActions } from "api/actions";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { usePoints } from "api/points";
import { DynamicSearchType } from "components/FiltersV2/DynamicSearch";
import { FormButtons } from "components/FormControls/FormButtons";
import { TextInput } from "components/FormControls/TextInput";
import { parseFiltersForExport } from "components/Layout/Export/helpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SelectedFilter } from "types/filters";
import { ImportTypes } from "types/import";
import { PointsTransferTypes, UnitTransferModal } from "types/unit-transfers";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { pluralizeUnit } from "utilities/strings";
import { AddUnitsModal } from "./AddUnitsModal";
import { Modals } from "./helpers";
import { ImportUnitsTransfersModal } from "./ImportUnitsTransfersModal";
const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    massActionInfo: {
      color: theme.gray500,
      "& p": {
        fontSize: 14,
      },
    },
    modalButtons: {
      justifyContent: "center",
      margin: theme.spacing(24, 0, 0),
      "& button": {
        flex: 1,
      },
    },
    modalInput: {
      marginTop: theme.spacing(12),
      width: "100%",
    },
  }),
);

interface UnitTransfersModalsProps {
  close: () => void;
  open: boolean;
  selectedModal?: UnitTransferModal;
  itemsLength?: number;
  setModal?: (modal: UnitTransferModal) => void;
  filters?: SelectedFilter[];
  dynamicSearchFilter?: DynamicSearchType;
}

export function UnitTransfersModals({
  close,
  setModal,
  open,
  selectedModal,
  itemsLength,
  filters,
  dynamicSearchFilter,
}: UnitTransfersModalsProps) {
  const classes = useStyles();
  const { message } = useMessage();
  const { activatePointsTransfer, cancelPointsTransfer, expirePointsTransfer } =
    usePoints();
  const { cancelTransfers, expireTransfers, activateTransfers } =
    useBulkActions();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const [errors, setErrors] = useState<any>();
  const handleCloseModal = () => {
    close();
    setErrors(null);
  };

  const handleBulkAction = (comment?: string) => {
    const isDynamicSearchFilterSet =
      dynamicSearchFilter && dynamicSearchFilter.value.length > 0;

    const mappedFilters = filters?.map((filter) => {
      // If main filters include the same filter from search box with "like" operator - search box has higher priority
      if (
        isDynamicSearchFilterSet &&
        filter.query === dynamicSearchFilter?.query &&
        filter.condition === "like"
      ) {
        return { ...filter, value: dynamicSearchFilter.value };
      } else {
        return filter;
      }
    });

    const actionPayload = {
      criteria: {
        ...(isDynamicSearchFilterSet && {
          [dynamicSearchFilter.query]: { like: dynamicSearchFilter.value },
        }),
        ...parseFiltersForExport(mappedFilters || []),
      },
      requestedAt: OLDate(new Date(), {
        formatString: "yyyy-MM-dd HH:mm:ssXXX",
      }),
      comment: comment,
    };

    switch (selectedModal?.mode) {
      case Modals.CancelTransfers:
        return cancelTransfers({
          cancelTransfers: actionPayload,
        });
      case Modals.ExpireTransfers:
        return expireTransfers({
          expireTransfers: actionPayload,
        });
      case Modals.ActivateTransfers:
        return activateTransfers({
          activateTransfers: actionPayload,
        });
      default:
        return null;
    }
  };

  return (
    <>
      {selectedModal?.mode === Modals.Add ? (
        <AddUnitsModal
          closeModal={() => close()}
          initialMode={PointsTransferTypes.ADD_POINTS}
          isModalOpen={open}
          setModal={() => setModal?.({ transfer: "", mode: Modals.Deduct })}
        />
      ) : selectedModal?.mode === Modals.Deduct ? (
        <AddUnitsModal
          closeModal={() => close()}
          initialMode={PointsTransferTypes.SPEND_POINTS}
          isModalOpen={open}
          setModal={() => setModal?.({ transfer: "", mode: Modals.Add })}
        />
      ) : selectedModal?.mode === Modals.Cancel ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await cancelPointsTransfer(
                selectedModal?.transfer,
              );
              if (response) {
                close();
                notify(message({ id: "transferCanceled" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          cancelText={message({ id: "exitDiscardChanges" })}
          close={() => handleCloseModal()}
          data-test-id="cancel-transfer-modal"
          errorInfo={errors?.[""]}
          header={message({ id: "cancelTransferInfo" })}
          info={message({ id: "thisActionCannotBeUndone" })}
          modalWidth={550}
          open={open}
          submitText={message({ id: "yesCancel" })}
        />
      ) : selectedModal?.mode === Modals.Activate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activatePointsTransfer(
                selectedModal?.transfer,
              );
              if (response) {
                close();
                notify(message({ id: "transferActivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          cancelText={message({ id: "exitDiscardChanges" })}
          close={() => handleCloseModal()}
          data-test-id="activate-transfer-modal"
          errorInfo={errors?.[""]}
          header={message({ id: "activateTransferInfo" })}
          modalWidth={550}
          open={open}
          submitText={message({ id: "yesActivate" })}
        />
      ) : selectedModal?.mode === Modals.Expire ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await expirePointsTransfer(
                selectedModal?.transfer,
              );
              if (response) {
                close();
                notify(message({ id: "transferExpired" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          cancelText={message({ id: "exitDiscardChanges" })}
          close={() => handleCloseModal()}
          data-test-id="expire-transfer-modal"
          errorInfo={errors?.[""]}
          header={message({ id: "expireTransferInfo" })}
          info={message({ id: "thisActionCannotBeUndone" })}
          modalWidth={536}
          open={open}
          submitText={message({ id: "yesExpire" })}
        />
      ) : selectedModal?.mode === Modals.ImportAddingUnitsTransfers ? (
        <ImportUnitsTransfersModal
          isModalOpen={open}
          type={ImportTypes.UNIT_TRANSFER_ADDING}
          closeModal={() => close()}
          setModal={(modal) => setModal?.(modal)}
        />
      ) : selectedModal?.mode === Modals.ImportSpendingUnitsTransfers ? (
        <ImportUnitsTransfersModal
          isModalOpen={open}
          type={ImportTypes.UNIT_TRANSFER_SPENDING}
          closeModal={() => close()}
          setModal={(modal) => setModal?.(modal)}
        />
      ) : selectedModal?.mode === Modals.CancelTransfers ||
        selectedModal?.mode === Modals.ExpireTransfers ||
        selectedModal?.mode === Modals.ActivateTransfers ? (
        <OLModal
          data-test-id="actions"
          header={message({ id: "massActionConfirmation" })}
          modalWidth={536}
          onClose={() => close()}
          open={open}
        >
          <Formik
            initialValues={{ comment: undefined }}
            onSubmit={async (values) => {
              try {
                const response = await handleBulkAction(values.comment);

                if (response) {
                  close();
                  notify(message({ id: "actionHasStartedInfo" }), {
                    button: {
                      text: message({ id: "viewList" }),
                      onClick: ({ closeNotification }) => {
                        closeNotification();

                        return navigate(links.importsExports, {
                          state: { activeTab: "massActionsLog" },
                        });
                      },
                    },
                  });
                }
              } catch (exception) {
                notify(getErrorMessage(exception));
                setErrors(backendErrors(getBackendErrors(exception)));
              }
            }}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <div className={classes.massActionInfo}>
                  <Typography>
                    {message(
                      { id: "massActionConfirmationInfo" },
                      {
                        count: pluralizeUnit(itemsLength, "record", "records"),
                      },
                    )}
                  </Typography>
                  <Typography>
                    {message({ id: "areYouSureYouWantToContinue" })}
                  </Typography>
                </div>
                <TextInput
                  className={classes.modalInput}
                  data-test-id="comment-input"
                  label={message({ id: "comment" })}
                  multiline
                  name="comment"
                  rows={4}
                />
                <FormButtons
                  className={classes.modalButtons}
                  disableSubmit={isSubmitting}
                  onCancelClick={() => close()}
                  onSubmitClick={() => handleSubmit()}
                  submitMessage={message({ id: "yesContinue" })}
                />
              </Form>
            )}
          </Formik>
        </OLModal>
      ) : null}
    </>
  );
}
